import React, { useEffect, useRef } from 'react';
import { CalculatorAdditionalPaymentResultData } from './types';

interface CalculatorAdditionalPaymentChartData {
  cols: { id: string; label: string; type: string }[];
  rows: { c: ({ v: number | Date } | null)[] }[];
}

interface CalculatorAdditionalPaymentChartProps {
  data: CalculatorAdditionalPaymentResultData;
}

const CalculatorAdditionalPaymentChart: React.FC<CalculatorAdditionalPaymentChartProps> = ({
  data
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chart = useRef<google.visualization.LineChart | null>(null);
  const isChartLoaded = useRef(false);

  useEffect(() => {
    const drawChart = () => {
      if (chartRef.current && isChartLoaded.current && data?.years) {
        const processedData = data.years.map((item) => [
          new Date(item.year, 0),
          item.oldResidualDebt,
          item.newResidualDebt
        ]);

        const chartData: CalculatorAdditionalPaymentChartData = {
          cols: [
            { id: 'year', label: 'Jaar', type: 'date' },
            { id: 'oldResidualDebt', label: 'Oude restschuld', type: 'number' },
            { id: 'newResidualDebt', label: 'Nieuwe restschuld', type: 'number' }
          ],
          rows: processedData.map((row) => ({ c: row.map((v) => ({ v })) }))
        };

        // Dynamically generate ticks with a 5-year interval from the data
        const minYear = Math.min(...data.years.map((item) => item.year));
        const maxYear = Math.max(...data.years.map((item) => item.year));
        const ticksArray = [];
        for (let year = minYear; year <= maxYear; year += 5) {
          ticksArray.push(new Date(year, 0));
        }
        if (!ticksArray.some((date) => date.getFullYear() === maxYear)) {
          ticksArray.push(new Date(maxYear, 0)); // Ensure the last year is included
        }

        const chartOptions: google.visualization.LineChartOptions = {
          fontName: 'DINNextLTProLight',
          theme: 'material',
          fontSize: 16,
          interpolateNulls: true,
          curveType: 'function',
          legend: {
            position: 'top',
            alignment: 'end',
            textStyle: { color: '#000000', fontSize: 16 }
          },
          hAxis: {
            textStyle: { color: '#000000', fontSize: 14 },
            gridlines: { color: 'transparent' },
            format: 'yyyy',
            ticks: ticksArray
          },
          vAxis: {
            format: 'short',
            textStyle: { color: '#000000', fontSize: 14 },
            gridlines: { color: 'transparent' },
            baselineColor: 'transparent'
          },
          series: {
            0: { color: '#C61118' },
            1: { color: '#00325D' }
          },
          chartArea: {
            left: 50,
            top: 20,
            width: '90%',
            height: '80%'
          },
          pointSize: 5,
          backgroundColor: {
            fill: 'transparent'
          },
          tooltip: { textStyle: { color: '#000000' }, showColorCode: true }
        };

        const googleChartData = new google.visualization.DataTable(chartData);
        chart.current = new google.visualization.LineChart(chartRef.current);
        chart.current.draw(googleChartData, chartOptions);
      }
    };

    if (typeof google !== 'undefined' && google.visualization) {
      isChartLoaded.current = true;
      drawChart();
    } else {
      const script = document.createElement('script');
      script.src = 'https://www.gstatic.com/charts/loader.js';
      script.async = true;
      script.onload = () => {
        google.charts.load('current', { packages: ['corechart'] });
        google.charts.setOnLoadCallback(() => {
          isChartLoaded.current = true;
          drawChart();
        });
      };
      document.body.appendChild(script);

      return () => {
        const scripts = document.querySelectorAll(
          'script[src="https://www.gstatic.com/charts/loader.js"]'
        );
        scripts.forEach((s) => s.remove());
        isChartLoaded.current = false;
      };
    }
  }, [data]);

  return <div ref={chartRef} />;
};

export default CalculatorAdditionalPaymentChart;
